import { Box, Button, Flex, Heading, HStack, Stack, Tag, Text } from '@chakra-ui/react'
import {
  faCheckCircle,
  faExclamationCircle,
  faExternalLink,
  faInfoCircle,
  type IconDefinition,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { pluralize } from '@/utils/helpers'

import {
  PrimaryTrainingType,
  type SecondaryTrainingType,
  TutorSecondaryTrainingType,
  TutorTrainingSubmissionAssignmentPriority,
  UnsuspendSecondaryTrainingType,
} from '@/queries/constants'
import { useUser } from '@/client/context/user'
import { getPointsForUserEvent } from '@/client/services/progression'
import { getPersonalizedTrainingUrl } from '@/client/services/url'

import PointsTag from '@/components/Achievements/PointsTag'
import TextWithLines from '@/components/TextWithLines/TextWithLines'
import ModalV3 from '@/components/UI/Modal/ModalV3'
import Time from '@/components/UI/Time/Time'

import { SECONDARY_TRAINING_TYPE_TO_NAME } from './common'
import type { TrainingUI } from './types'

const NOTICE_ASSIGNMENT_REQUIRED = 'This training is required before you can host new sessions.'
const NOTICE_ASSIGNMENT_HIGHLY_RECOMMENDED =
  'This training has been assigned to you by a coach and is strongly recommended before hosting new sessions.'

type NoticeDetails = {
  noticeIcon: IconDefinition
  noticeIconColor: string
  noticeText: JSX.Element | string
  noticeBg: string
}

const DEFAULT_MODAL_DETAILS: NoticeDetails = {
  noticeIcon: faInfoCircle,
  noticeIconColor: 'fg.subtle',
  noticeText: 'This is an optional training. Feel free to take it to upskill your tutoring!',
  noticeBg: 'bg.muted',
}

const REQUIRED_MODAL_UI_DETAILS: Omit<NoticeDetails, 'noticeText'> = {
  noticeIcon: faInfoCircle,
  noticeIconColor: 'secondary.fg',
  noticeBg: 'secondary.subtle',
}

const UNSUSPEND_MODAL_UI_DETAILS: Omit<NoticeDetails, 'noticeText'> = {
  noticeIcon: faInfoCircle,
  noticeIconColor: 'error.fg',
  noticeBg: 'error.subtle',
}

const MODAL_ALERT_DETAILS_BY_SECONDARY_TRAINING_TYPE: Record<
  SecondaryTrainingType,
  NoticeDetails | null
> = {
  [TutorSecondaryTrainingType.LIVE_HELP]: {
    ...REQUIRED_MODAL_UI_DETAILS,
    noticeText: 'This training is required to host a Live Help session.',
  },
  [TutorSecondaryTrainingType.SAT]: {
    ...REQUIRED_MODAL_UI_DETAILS,
    noticeText: 'This training is required to host a SAT Prep session or series.',
  },
  [TutorSecondaryTrainingType.SAT_BOOTCAMP]: {
    ...REQUIRED_MODAL_UI_DETAILS,
    noticeText: 'This training is required to host an SAT Bootcamp.',
  },
  [TutorSecondaryTrainingType.SAT_BOOTCAMP_ONBOARDING]: {
    ...REQUIRED_MODAL_UI_DETAILS,
    noticeText: 'This training is required to host an SAT Bootcamp Onboarding Session.',
  },
  [TutorSecondaryTrainingType.COLLEGE_ADMISSIONS_WORKSHOP]: {
    ...REQUIRED_MODAL_UI_DETAILS,
    noticeText: 'This training is required to host a College Admissions Workshop.',
  },
  [TutorSecondaryTrainingType.ONBOARDING]: null,
  [TutorSecondaryTrainingType.ENRICHMENT]: DEFAULT_MODAL_DETAILS,
  [TutorSecondaryTrainingType.SKILL_BUILDER]: null,
  [TutorSecondaryTrainingType.LEARNING_LABS]: {
    ...REQUIRED_MODAL_UI_DETAILS,
    noticeText: 'This training is required to host a Khan Academy Learning Labs series.',
  },
  [UnsuspendSecondaryTrainingType.LEARNER_ATTENDANCE]: {
    ...UNSUSPEND_MODAL_UI_DETAILS,
    noticeText: 'This training is required to recover your account.',
  },
}

export default function TrainingModal({
  training,
  onClose,
}: {
  training: TrainingUI
  onClose: () => void
}): JSX.Element {
  const user = useUser()

  let alertDetails = MODAL_ALERT_DETAILS_BY_SECONDARY_TRAINING_TYPE[training.secondaryType]

  if (
    training.submission?.assignmentPriority === TutorTrainingSubmissionAssignmentPriority.REQUIRED
  ) {
    alertDetails = {
      noticeIcon: faExclamationCircle,
      noticeIconColor: 'error.fg',
      noticeText: NOTICE_ASSIGNMENT_REQUIRED,
      noticeBg: 'error.subtle',
    }
  }

  if (
    training.submission?.assignmentPriority ===
    TutorTrainingSubmissionAssignmentPriority.HIGHLY_RECOMMENDED
  ) {
    alertDetails = {
      noticeIcon: faExclamationCircle,
      noticeIconColor: 'warning.fg',
      noticeText: NOTICE_ASSIGNMENT_HIGHLY_RECOMMENDED,
      noticeBg: 'warning.subtle',
    }
  }

  if (training.submission?.submittedAt != null) {
    alertDetails = {
      noticeIcon: faCheckCircle,
      noticeIconColor: 'success.fg',
      noticeText: (
        <>
          You completed this training on <Time time={training.submission.submittedAt.toISO()} />
        </>
      ),
      noticeBg: 'success.subtle',
    }
  }

  const points = getPointsForUserEvent({
    type: 'tutor_training_completed',
    payload: {
      trainingId: -1,
    },
  })

  return (
    <ModalV3
      size="3xl"
      open={training != null}
      onClose={onClose}
      title={training?.title ?? ''}
      body={() => (
        <Flex direction="column">
          {alertDetails == null ? null : (
            <HStack
              py={2}
              px={4}
              mb={6}
              spacing={3}
              borderRadius="8px"
              direction="row"
              backgroundColor={alertDetails.noticeBg}
            >
              <FontAwesomeIcon
                icon={alertDetails.noticeIcon}
                color={chakraTokenToVar(alertDetails.noticeIconColor)}
              />
              <Text>{alertDetails.noticeText}</Text>
            </HStack>
          )}
          {training.submission?.assignmentNote == null ? null : (
            <Stack
              p={6}
              mb={6}
              spacing={4}
              direction="column"
              width="100%"
              borderRadius="8px"
              border="2px solid"
              borderColor="border"
            >
              <Heading size="sm" mb={2}>
                Notes
              </Heading>
              <Text>{training.submission.assignmentNote}</Text>
              <Text fontSize="small">
                Assigned to you by a coach on <Time time={training.createdAt} format="DD" />
              </Text>
            </Stack>
          )}
          <Stack direction="row" justify="space-between">
            <HStack spacing={2}>
              <Tag>{SECONDARY_TRAINING_TYPE_TO_NAME[training.secondaryType]}</Tag>
              <Text color="fg.muted" fontWeight="semibold">
                {pluralize(training.timeEstimateMinutes, 'min')}
              </Text>
            </HStack>
            {training.primaryType !== PrimaryTrainingType.TUTOR ? null : (
              <Box>
                <PointsTag
                  points={points}
                  tooltip={
                    training.submission?.submittedAt == null
                      ? `Get ${pluralize(
                          points,
                          'Schoolhouse Point'
                        )} for completing this training.`
                      : `You received ${pluralize(
                          points,
                          'Schoolhouse Point'
                        )} for completing this training.`
                  }
                />
              </Box>
            )}
          </Stack>
          <Flex mt={6} direction="column">
            <Heading size="sm" mb={2}>
              Description
            </Heading>
            <Text mb={2}>{training.excerpt}</Text>
            <TextWithLines text={training.description} maxLines={Number.POSITIVE_INFINITY} />
          </Flex>
        </Flex>
      )}
      rightFooterContent={() =>
        training.submission?.submittedAt == null ? (
          <Button
            as="a"
            target="_blank"
            rel="noreferrer"
            onClick={onClose}
            href={getPersonalizedTrainingUrl({
              userId: user.id,
              uuidTraining: user.uuidTraining,
              trainingLink: training.link,
              trainingSlug: training.slug,
              worldSlug: null,
            })}
            rightIcon={<FontAwesomeIcon icon={faExternalLink} />}
          >
            Start Training
          </Button>
        ) : (
          <Button
            as="a"
            target="_blank"
            rel="noreferrer"
            onClick={onClose}
            href={training.linkSummary}
            rightIcon={<FontAwesomeIcon icon={faExternalLink} />}
          >
            Review Training
          </Button>
        )
      }
    />
  )
}
